import { createRouter, createWebHistory } from "vue-router";

import AccountingRoutes from "@/modules/accounting/routes";
import AdminRoutes from "@/modules/admin/routes";
import AppsRoutes from "@/modules/apps/routes";
import BankingRoutes from "@/modules/banking/routes";
import BackgroundTasksRoutes from "@/modules/btasks/routes";
import CallRoutes from "@/modules/calls/routes";
import ClientRoutes from "@/modules/clients/routes";
import DashboardRoutes from "@/modules/dashboard/routes";
import EmailsRoutes from "@/modules/emails/routes";
import EventsRoutes from "@/modules/events/routes";
import FinancesRoutes from "@/modules/finances/routes";
import FitRoutes from "@/modules/fit/routes";
import HotelsRoutes from "@/modules/hotels/routes";
import HumanResourcesRoutes from "@/modules/hr/routes";
import LeadRoutes from "@/modules/leads/routes";
import LocationRoutes from "@/modules/locations/routes";
import LogsRoutes from "@/modules/logs/routes";
import MailingsRoutes from "@/modules/mailings/routes";
import MarketingRoutes from "@/modules/marketing/routes";
import PaymentRoutes from "@/modules/payments/routes";
import ProductsRoutes from "@/modules/products/routes";
import ReportRoutes from "@/modules/reports/routes";
import SuppliersRoutes from "@/modules/suppliers/routes";
import TagRoutes from "@/modules/tags/routes";
import TasksRoutes from "@/modules/tasks/routes";
import UsersRoutes from "@/modules/users/routes";
import { useAuthStore } from "@/stores/auth";

const router = createRouter({
    history: createWebHistory(),
    linkExactActiveClass: "is-active",
    routes: [
        {
            name: "quote_group_submit",
            path: "/hotels/quotes/group/:reference/details",
            redirect: {
                path: "login",
            },
        },
        {
            name: "restartClient",
            path: "/restart",
            component: () => import("@/views/RestartPage.vue"),
            meta: { noAuth: true },
        },
        {
            path: "/",
            component: () => import("@/views/structure/BaseLayout.vue"),
            redirect: {
                path: "dashboard",
            },
            children: [
                {
                    name: "login",
                    path: "/login",
                    component: () => import("@/views/LoginPage.vue"),
                    meta: { noAuth: true, skipAuth: true },
                },
                {
                    name: "terms_and_conditions",
                    path: "/terms",
                    component: () => import("@/views/TermsAndConditions.vue"),
                },
                {
                    name: "not_found",
                    path: "404",
                    component: () => import("@/views/NotFound.vue"),
                },
            ].concat(
                AccountingRoutes,
                AdminRoutes,
                AppsRoutes,
                BackgroundTasksRoutes,
                BankingRoutes,
                CallRoutes,
                ClientRoutes,
                DashboardRoutes,
                EmailsRoutes,
                EventsRoutes,
                FinancesRoutes,
                FitRoutes,
                HotelsRoutes,
                HumanResourcesRoutes,
                LeadRoutes,
                LocationRoutes,
                LogsRoutes,
                MailingsRoutes,
                MarketingRoutes,
                ReportRoutes,
                SuppliersRoutes,
                TagRoutes,
                TasksRoutes,
                ProductsRoutes,
                UsersRoutes,
                PaymentRoutes,
            ),
        },
        {
            path: "/:pathMatch(.*)*",
            redirect: { path: "/404" },
        },
    ],
});

export const matchRoute = (name) => {
    const matches = router.resolve({ name }).matched;
    if (matches.length > 0) {
        for (const route of matches) {
            if (route.name === name) {
                return route;
            }
        }
    }
    return undefined;
};

const paramsCache = {};

export const routeParams = (name) => {
    if (paramsCache[name] === undefined) {
        const route = matchRoute(name);
        if (route !== undefined) {
            paramsCache[name] = route.path
                .split("/")
                .filter((x) => x.charAt(0) === ":")
                .map((x) => x.substring(1));
        } else {
            paramsCache[name] = null;
        }
    }
    return paramsCache[name] || undefined;
};

router.beforeEach((to, _, next) => {
    const authStore = useAuthStore();

    if (authStore.loggedIn) {
        if (to.matched.some((record) => record.meta.skipAuth)) {
            next({
                path: "/dashboard",
                query: { redirect: to.fullPath },
            });
        } else {
            next();
        }
    } else {
        if (to.matched.some((record) => record.meta.noAuth)) {
            next();
        } else {
            next({
                path: "/login",
                query: { redirect: to.fullPath },
            });
        }
    }
});

export default router;
